import {
  CurrencyPipe,
  DatePipe,
  DecimalPipe,
  TitleCasePipe,
  registerLocaleData
} from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeES from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { LOCALE_ID, enableProdMode, importProvidersFrom } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular
} from '@ionic/angular/standalone';
import { CustomPaginator } from '@material/lib/custom-paginator';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxEditorModule } from 'ngx-editor';
import { provideMarkdown } from 'ngx-markdown';

import { APP_ROUTES } from '@app/app.routes';
import { authInterceptor } from '@app/core/interceptors/interceptor';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

registerLocaleData(localeES);
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeEn);
const ANGULAR_MODULES = [BrowserAnimationsModule, BrowserModule];
const GLOBAL_PIPES = [DatePipe, DecimalPipe, TitleCasePipe, CurrencyPipe];

// Traducción al español de los labels/tooltips de ngx-editor. Se registra a
// nivel global porque NgxEditorService está `providedIn: 'root'` y no acepta
// override desde providers de componente. Las claves snake_case vienen
// impuestas por ngx-editor y no se pueden renombrar.
// TODO(i18n): Si ngx-editor se usa fuera de backoffice, sustituir
// este registro global por un wrapper que reactive los locals según
// el idioma activo de TranslateService (ngx-translate).
/* eslint-disable @typescript-eslint/naming-convention */
const NGX_EDITOR_ES_LOCALS = {
  bold: 'Negrita',
  italic: 'Cursiva',
  ordered_list: 'Lista numerada',
  bullet_list: 'Lista con viñetas',
  heading: 'Encabezado',
  h1: 'Título 1',
  h2: 'Título 2',
  h3: 'Título 3',
  h4: 'Título 4',
  h5: 'Título 5',
  h6: 'Título 6',
  align_left: 'Alinear a la izquierda',
  align_center: 'Centrar',
  align_right: 'Alinear a la derecha',
  align_justify: 'Justificar'
};
/* eslint-enable @typescript-eslint/naming-convention */

if (environment.production) {
  enableProdMode();
}
bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      rippleEffect: false,
      mode: 'ios'
    }),
    //Rutas
    provideRouter(APP_ROUTES),
    provideHttpClient(withInterceptors([authInterceptor])),
    // Traducción
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'es',
      lang: 'es'
    }),
    importProvidersFrom(
      ...ANGULAR_MODULES,
      NgxEditorModule.forRoot({ locals: NGX_EDITOR_ES_LOCALS })
    ),
    { provide: LOCALE_ID, useValue: 'es' },
    // Firebase
    provideFirebaseApp(() => initializeApp({ ...environment.firebaseConfig })),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    // Chart.js
    provideCharts(withDefaultRegisterables()),
    // Tabla material
    {
      provide: MatPaginatorIntl,
      useClass: CustomPaginator
    },
    provideNativeDateAdapter(),
    // Markdown (chat de análisis de documentos)
    provideMarkdown(),
    // PIPES
    ...GLOBAL_PIPES
  ]
}).catch((err: unknown) => console.log(err));
